import React, { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { makeStyles, Theme } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import GET_KEYBOARDING_STUDENT_SETTINGS from 'graph/settings/key/get-keyboarding-student-settings-query';
import UPDATE_KEY_STUDENT_HOME_USE_SETTING from 'graph/settings/key/update-key-student-home-use-setting-mutation';
import { updateCache } from 'graph/update-cache';
import { KeySettingHomeUseData, Student } from 'types/schema-generated-types';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import EmailInput, { OnChangeArgs } from 'components/@common/Inputs/EmailInput';

interface HomeAccessProps {
  student: Student;
  productCurriculumCode: string;
  setting: KeySettingHomeUseData & { [key: string]: keyof KeySettingHomeUseData };
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

// const EMAIL_TEMPLATE =
//   'Dear Parent or Guardian\n\nIn our class this year, STUDENT_NAME is using Keyboarding Without Tears to learn keyboarding skills, computer literacy and digital citizenship. One of the features of the program is that students can use the student license they have from school for practice outside the school. To access the program, you will need a laptop or desktop with internet access.';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: { padding: theme.spacing(2), display: 'flex' },
    studentForm: { display: 'flex', marginTop: theme.spacing(2) },
    studentInfo: { display: 'flex', flexDirection: 'column', flex: '70%' },
    studentLabel: { fontWeight: theme.typography.fontWeightBold },
    studentTemplate: { marginTop: theme.spacing(3) },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    }
  };
});

const getStudentKeySettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (cachedData, { updateKeyStudentSettingHomeUse }): { keySettingSettings: any } => {
        const [{ value: homeUse }] = updateKeyStudentSettingHomeUse;
        return {
          keySettingSettings: {
            ...cachedData?.keySettingSettings,
            homeUse
          }
        };
      }
    }
  ])
});

const HomeAccess: FC<HomeAccessProps> = ({
  student,
  productCurriculumCode,
  setting,
  expanded,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('HomeAccessSetting');
  const classes = useStyles();
  const [, { openAlert }] = useStore();

  const isHomeAccessEnabled = !!setting?.[productCurriculumCode]; // Enabled if there is an access code for active product.
  const [enableHomeAccess, setEnableHomeAccess] = useState(isHomeAccessEnabled);
  const [contactEmail, setContactEmail] = useState(
    (isHomeAccessEnabled && setting?.contactEmail) || ''
  );
  const [contactError, setContactError] = useState<string | null>(null);

  const { firstName, lastName, password } = student;

  const [updateKeyStudentSettingHomeUse] = useMutation(
    UPDATE_KEY_STUDENT_HOME_USE_SETTING,
    getStudentKeySettingsMutationCacheUpdate(student.id)
  );

  const handleToggle = (): void => setEnableHomeAccess(!enableHomeAccess);

  const handleEmailContactChange = ({ value }: OnChangeArgs): void => {
    setContactEmail(value);
  };

  const handleEmailContactError = (err: string | null): void => {
    setContactError(err);
  };

  const handleOnCancel = (): void => {
    setEnableHomeAccess(!!setting?.[productCurriculumCode] ?? false);
    setContactEmail(setting?.contactEmail || '');
    setContactError(null);
  };

  const handleOnSubmit = async (): Promise<void> => {
    if (contactError) {
      return;
    }

    try {
      await updateKeyStudentSettingHomeUse({
        variables: {
          data: {
            studentId: student.id,
            firstName,
            lastName,
            password,
            value: {
              productCurriculumCode,
              ...(enableHomeAccess && { contactEmail })
            }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Home Access', t));
    } catch (error) {
      console.log('Error');
    }
  };

  return (
    <SettingsCard
      title={t('Home Access')}
      description={t('Send student login information via email to parents and/or guardians.')}
      expanded={expanded}
      panel="homeAccess"
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnSubmit}
      applyLabel={enableHomeAccess ? t('Apply Changes & Send Email') : t('Apply Changes')}
      disabled={Boolean(contactError) || (enableHomeAccess && !contactEmail)}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormLabel component="label" className={classes.label}>
          {t('Home Access')}
        </FormLabel>
        <FormControlLabel
          control={
            <Switch
              checked={enableHomeAccess}
              onChange={handleToggle}
              name="homeAccess"
              color="primary"
            />
          }
          label={enableHomeAccess ? t('Enabled') : t('Disabled')}
        />
        {enableHomeAccess && (
          <div>
            <div className={classes.studentForm}>
              <div className={classes.studentInfo}>
                <Typography variant="body1" className={classes.studentLabel}>
                  {t('STUDENT NAME')}
                </Typography>
                <Typography
                  variant="body1"
                  component="p"
                >{`${student.firstName} ${student.lastName}`}</Typography>
              </div>
              <EmailInput
                variant={'outlined'}
                label={t('PARENT EMAIL')}
                placeholder={t('Email Address')}
                onChange={handleEmailContactChange}
                onValidation={handleEmailContactError}
                error={contactError}
                value={contactEmail}
                required={true}
              />
            </div>
          </div>
        )}
      </FormControl>
    </SettingsCard>
  );
};

export default HomeAccess;
